import type { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

import {
  clientAssessmentSchema,
  clientPhiSchema,
  clientSchema,
  createDormantClientResultSchema,
  redFlagSchema,
  type Client,
  type ClientAssessment,
  type ClientListParams,
  type ClientPhi,
  type CreateDormantClientInput,
  type CreateDormantClientResult,
  type PaginatedClients,
  type RedFlag,
  type UpdateClientNonPhiInput,
  type UpdateClientPhiInput,
} from '../types';

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[clientsService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

function stripEmptyStrings<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v === undefined) continue;
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

export const clientsService = {
  async list(params: ClientListParams = {}): Promise<PaginatedClients> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>('/clients', { params });
    const rows: Client[] = data.data.map((row) => parseOrThrow(clientSchema, row, 'list'));
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  async get(id: string): Promise<Client> {
    const { data } = await apiClient.get<{ data: unknown }>(`/clients/${id}`);
    return parseOrThrow(clientSchema, data.data, 'get');
  },

  async getPhi(id: string): Promise<ClientPhi> {
    const { data } = await apiClient.get<{ data: unknown }>(`/clients/${id}/phi`);
    return parseOrThrow(clientPhiSchema, data.data, 'getPhi');
  },

  async updateNonPhi(id: string, input: UpdateClientNonPhiInput): Promise<Client> {
    const payload = stripEmptyStrings(input);
    await apiClient.patch(`/clients/${id}`, payload);
    return clientsService.get(id);
  },

  async updatePhi(id: string, input: UpdateClientPhiInput): Promise<Client> {
    const payload = stripEmptyStrings(input);
    await apiClient.patch(`/clients/${id}`, payload);
    return clientsService.get(id);
  },

  async listRedFlags(clientId: string): Promise<RedFlag[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>(`/clients/${clientId}/red-flags`);
    return data.data.map((row) => parseOrThrow(redFlagSchema, row, 'listRedFlags'));
  },

  async listAssessments(clientId: string): Promise<ClientAssessment[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>(`/clients/${clientId}/assessments`);
    return data.data.map((row) => parseOrThrow(clientAssessmentSchema, row, 'listAssessments'));
  },

  async createShadow(input: CreateDormantClientInput): Promise<CreateDormantClientResult> {
    const nameParts = (input.name ?? '').trim().split(/\s+/).filter(Boolean);
    const [firstName, ...rest] = nameParts;
    const payload = stripEmptyStrings({
      email: input.email?.trim().toLowerCase(),
      phoneNumber: input.phoneNumber,
      firstName,
      lastName: rest.join(' ') || undefined,
      organizationId: input.organizationId,
      locationId: input.locationId,
    });
    const { data } = await apiClient.post<{ data: unknown }>('/clients/shadow', payload);
    return parseOrThrow(createDormantClientResultSchema, data.data, 'createShadow');
  },
};
